import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from './assests/logoblack.png';
import './Navbar.css';

const navLinks = [
  // { name: 'Home',    path: '/' },
  { name: 'Product', path: '/rusiv' },
  { name: 'About',   path: '/about' },
  { name: 'Vision',  path: '/vision' },
  // Careers is intentionally left out of primary nav while the page shows
  // "no open roles currently" (see CareersPage.js). It stays reachable via
  // the Footer and direct link. Re-add here once roles open.
  // { name: 'Careers', path: '/careers' },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isOpen,   setIsOpen]   = useState(false);
  const [hoveredPath, setHoveredPath] = useState(null);
  const location = useLocation();

  /* ── Scroll detection ─────────────────────────────────────── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Close menu on route change ───────────────────────────── */
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  /* ── Close on Escape key ──────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ── Lock body scroll while mobile menu is open ───────────── */
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const handleLinkClick = (path) => {
    if (isActive(path)) window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <>
      {/* ── Navbar bar ──────────────────────────────────────── */}
      <nav
        className={`nb${scrolled ? ' nb--scrolled' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="nb-inner">

          {/* Brand */}
          <Link
            to="/"
            className="nb-brand"
            onClick={() => handleLinkClick('/')}
            aria-label="Allyverto home"
          >
            <img src={logo} alt="Allyverto logo" className="nb-logo" />
            <span className="nb-brand-text">Allyverto</span>
          </Link>

          {/* Desktop links */}
          <ul className="nb-links" onMouseLeave={() => setHoveredPath(null)}>
            {navLinks.map(({ name, path }) => (
              <li
                key={path}
                onMouseEnter={() => setHoveredPath(path)}
              >
                {hoveredPath === path && (
                  <motion.span
                    className="nb-hover-pill"
                    layoutId="nb-hover-pill"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  />
                )}
                <Link
                  to={path}
                  className={`nb-link${isActive(path) ? ' nb-link--active' : ''}`}
                  onClick={() => handleLinkClick(path)}
                  onFocus={() => setHoveredPath(path)}
                  onBlur={() =>
                    setHoveredPath((current) => (current === path ? null : current))
                  }
                  aria-current={isActive(path) ? 'page' : undefined}
                >
                  {name}
                </Link>
              </li>
            ))}
          </ul>

          {/* Desktop CTA */}
          <div className="nb-actions">
            <a
              href="https://rusiv.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="nb-cta"
              onClick={() => handleLinkClick('/rusiv')}
            >
              Meet Rusiv
            </a>
          </div>

          {/* Hamburger button */}
          <button
            className={`nb-toggle${isOpen ? ' nb-toggle--open' : ''}`}
            onClick={() => setIsOpen((v) => !v)}
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isOpen}
            aria-controls="nb-mobile-menu"
          >
            <span className="nb-bar" />
            <span className="nb-bar" />
            <span className="nb-bar" />
          </button>

        </div>
      </nav>

      {/* ── Backdrop overlay ────────────────────────────────── */}
      <div
        className={`nb-overlay${isOpen ? ' nb-overlay--visible' : ''}`}
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* ── Mobile menu panel ───────────────────────────────── */}
      <div
        id="nb-mobile-menu"
        className={`nb-mobile${isOpen ? ' nb-mobile--open' : ''}${scrolled ? ' nb-mobile--compact' : ''}`}
        role="navigation"
        aria-label="Mobile navigation"
        aria-hidden={!isOpen}
      >
        <ul className="nb-mobile-links">
          {navLinks.map(({ name, path }, i) => (
            <li key={path} style={{ '--i': i }}>
              <Link
                to={path}
                className={`nb-mobile-link${isActive(path) ? ' nb-mobile-link--active' : ''}`}
                onClick={() => handleLinkClick(path)}
                aria-current={isActive(path) ? 'page' : undefined}
                tabIndex={isOpen ? 0 : -1}
              >
                {name}
              </Link>
            </li>
          ))}
        </ul>

        <div className="nb-mobile-footer">
          <a
            href="https://rusiv.com/"
            className="nb-mobile-cta"
            onClick={() => handleLinkClick('/rusiv')}
            tabIndex={isOpen ? 0 : -1}
          >
            Meet Rusiv
          </a>
        </div>
      </div>
    </>
  );
};

export default Navbar;
